// Claim Offer Widget JavaScript
(() => {
  // Cache DOM elements once
  const elements = {
    widget: document.querySelector('[data-offer-widget]'),
    get percentage() { return document.querySelector('.claim-offer-widget__percentage') },
    get amount() { return document.querySelector('.claim-offer-widget__amount') },
    get claimButton() { return elements.widget?.querySelector('.claim-offer-widget__button') },
    get textDisplay() { return elements.widget?.querySelector('.claim-offer-widget__text') }
  };
  
  // Cache settings
  const settings = window.themeSettings?.claimOffer || {};
  
  // Utility functions
  const formatMoney = cents => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  const calculateDiscount = (price, percentage) => price && percentage ? Math.round((price * percentage) / 100) : 0;
  
  // Make core functions globally available
  window.claimOfferWidget = {
    getActiveOffer() {
      if (typeof this._getActiveOffer === 'function') return this._getActiveOffer();
      
      const utmSource = new URLSearchParams(window.location.search).get('utm_source') || 
                       sessionStorage.getItem('ghf_utm_source');
      if (!utmSource) return null;
      
      const offers = settings.offers || [];
      return offers.find(offer => 
        offer.sources.some(source => utmSource.toLowerCase().includes(source.toLowerCase()))
      );
    },

    showDiscountCode(code) {
      if (!elements.claimButton) return;
      
      // Store claimed state
      sessionStorage.setItem('ghf_claimed_offer', code);
      sessionStorage.setItem('ghf_claimed_timestamp', new Date().toISOString());

      // Update display text
      if (elements.textDisplay) {
        elements.textDisplay.textContent = elements.textDisplay.textContent.replace('Get ', '');
      }

      // Create new button with code display
      const newButton = elements.claimButton.cloneNode(true);
      const container = document.createElement('div');
      container.style.cssText = 'display:flex;align-items:center;justify-content:center;gap:8px;width:100%;cursor:pointer';
      container.classList.add('claim-offer-widget__button'); // Add the same class for querying
      
      const codeDisplay = document.createElement('div');
      codeDisplay.textContent = code;
      codeDisplay.style.cssText = 'font-weight:bold;font-size:16px';
      
      const copyButton = document.createElement('button');
      copyButton.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>';
      copyButton.style.cssText = 'background:none;border:none;cursor:pointer;padding:4px;display:flex;align-items:center';
      
      // Add click handlers
      container.addEventListener('click', () => {
        fetch('/discount/' + code).then(() => {
          container.style.backgroundColor = '#4CAF50';
          container.style.color = 'white';
          setTimeout(() => {
            container.style.backgroundColor = '';
            container.style.color = '';
          }, 1000);
        });
      });

      copyButton.addEventListener('click', e => {
        e.stopPropagation();
        navigator.clipboard.writeText(code).then(() => {
          copyButton.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4CAF50" stroke-width="2"><path d="M20 6L9 17l-5-5"></path></svg>';
          setTimeout(() => {
            copyButton.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>';
          }, 2000);
        });
      });

      // Assemble and display
      container.appendChild(codeDisplay);
      container.appendChild(copyButton);
      
      // Preserve the percentage and amount elements
      const percentage = elements.claimButton.querySelector('.claim-offer-widget__percentage');
      const amount = elements.claimButton.querySelector('.claim-offer-widget__amount');
      elements.claimButton.innerHTML = '';
      if (percentage) elements.claimButton.appendChild(percentage);
      if (amount) elements.claimButton.appendChild(amount);
      elements.claimButton.appendChild(container);
      
      // Auto-apply discount
      fetch('/discount/' + code);
    }
  };

  function updateDiscountAmount(variant, offer) {
    if (!variant) {
      const product = window.productJSON;
      if (!product) return;
      
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get('variant');
      
      variant = variantId ? 
        product.variants.find(v => v.id.toString() === variantId) :
        product.variants.find(v => v.available) || product.variants[0];
    }
    
    if (!variant?.price) return;
    
    const discountAmount = calculateDiscount(variant.price, offer.percentage);
    const formattedAmount = formatMoney(discountAmount);
    const textEl = elements.widget?.querySelector('.claim-offer-widget__text');
    
    if (textEl) {
      // Check if code was claimed (no "Get" prefix)
      const isClaimed = textEl.textContent.trim().indexOf('Get') !== 0;
      const prefix = isClaimed ? '' : 'Get ';
      textEl.textContent = `${prefix}${offer.percentage}% Off (${formattedAmount})`;
    }
  }

  function initializeWidget() {
    const activeOffer = window.claimOfferWidget.getActiveOffer();
    if (!activeOffer || !elements.widget) return;

    // Add mobile-only style
    if (!document.getElementById('mobile-widget-style')) {
      const styleTag = document.createElement('style');
      styleTag.id = 'mobile-widget-style';
      styleTag.textContent = `
        @media (min-width: 768px) {
          [data-offer-widget] {
            display: none !important;
          }
        }
      `;
      document.head.appendChild(styleTag);
    }

    elements.widget.style.display = 'block';
    
    // Initialize display and setup variant handling first
    updateDiscountAmount(null, activeOffer);
    
    // Setup variant change handling - ALWAYS do this
    document.addEventListener('variant:change', function(event) {
      const variant = event.detail?.variant;
      if (!variant?.price) return;
      
      // Always update the discount amount
      updateDiscountAmount(variant, activeOffer);
      
      // If code is claimed, ensure it's still applied
      const claimedCode = sessionStorage.getItem('ghf_claimed_offer');
      if (claimedCode && claimedCode === activeOffer.code) {
        fetch('/discount/' + claimedCode);
      }
    });
    
    // Check for claimed state
    const claimedCode = sessionStorage.getItem('ghf_claimed_offer');
    if (claimedCode && claimedCode === activeOffer.code) {
      window.claimOfferWidget.showDiscountCode(claimedCode);
      return;
    }
    
    // Setup Klaviyo handling
    window.addEventListener('klaviyoForms', e => {
      if (e.detail?.type === 'submit') {
        window.claimOfferWidget.showDiscountCode(activeOffer.code);
      }
    });

    // Setup claim button
    elements.claimButton?.addEventListener('click', () => {
      const formId = settings.isMobile?.() ? 
        activeOffer.klaviyo_form_id_mobile : 
        activeOffer.klaviyo_form_id_desktop;
      
      if (formId) {
        window._klOnsite = window._klOnsite || [];
        window._klOnsite.push(['openForm', formId]);
      }
    });
  }

  // Initialize when ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeWidget);
  } else {
    initializeWidget();
  }
})();